import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, 
  Download, 
  Trash2, 
  Send, 
  Loader2, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Search,
  Eye,
  FileArchive,
  Clock,
  Building2,
  Printer,
  Check,
  X,
  Info,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  FileWarning,
  Ban,
  FileX,
  Edit3,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const GerenciarNFePage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [notas, setNotas] = useState([]);
  const [filtros, setFiltros] = useState({
    status: 'todos',
    dataInicio: '',
    dataFim: ''
  });
  const [notaSelecionada, setNotaSelecionada] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [showConfirmExcluir, setShowConfirmExcluir] = useState(false);
  const [showErroReenvio, setShowErroReenvio] = useState(false);
  const [erroReenvio, setErroReenvio] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [processandoId, setProcessandoId] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null);
  const [showConsultaSefaz, setShowConsultaSefaz] = useState(false);
  const [consultaSefazResult, setConsultaSefazResult] = useState(null);
  const [consultandoSefazId, setConsultandoSefazId] = useState(null);

  // Estados para Cancelar NF-e
  const [showCancelar, setShowCancelar] = useState(false);
  const [justificativaCancelamento, setJustificativaCancelamento] = useState('');
  
  // Estados para Inutilizar
  const [showInutilizar, setShowInutilizar] = useState(false);
  const [inutilizarForm, setInutilizarForm] = useState({
    serie: 1,
    numero_inicial: '',
    numero_final: '',
    justificativa: ''
  });
  
  // Estados para Carta de Correção
  const [showCartaCorrecao, setShowCartaCorrecao] = useState(false);
  const [textoCorrecao, setTextoCorrecao] = useState('');
  const [cartasCorrecaoLista, setCartasCorrecaoLista] = useState([]);
  const [showListaCartas, setShowListaCartas] = useState(false);
  
  // Estados para Inutilizações
  const [inutilizacoes, setInutilizacoes] = useState([]);

  // Estados para Editar Itens (corrigir nota antes de enviar)
  const [showEditarItens, setShowEditarItens] = useState(false);
  const [itensEditados, setItensEditados] = useState([]);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  // Debug XML (para investigar erros de schema 225 da SEFAZ)
  const [showDebugXml, setShowDebugXml] = useState(false);
  const [debugXmlContent, setDebugXmlContent] = useState('');
  const [debugXmlLoading, setDebugXmlLoading] = useState(false);
  const [showListaInutilizacoes, setShowListaInutilizacoes] = useState(false);

  // Estados para Editar Cabeçalho (natureza op, destinatário, frete, info adicionais)
  const [showEditarCabecalho, setShowEditarCabecalho] = useState(false);
  const [cabecalhoEditado, setCabecalhoEditado] = useState({
    natureza_operacao: '',
    destinatario: {
      cpf_cnpj: '', nome: '', ie: '', indIEDest: '9',
      endereco: { logradouro: '', numero: '', complemento: '', bairro: '',
                  cidade: '', cod_municipio: '', uf: '', cep: '' }
    },
    frete: { modalidade: '9' },
    info_adicionais: ''
  });
  const [salvandoCabecalho, setSalvandoCabecalho] = useState(false);

  // Carregar diagnóstico
  const carregarDiagnostico = useCallback(async () => {
    try {
      const response = await api.get('/nfe/diagnostico');
      setDiagnostico(response.data);
    } catch (error) {
      console.error('Erro ao carregar diagnóstico:', error);
    }
  }, []);

  // Carregar notas
  const carregarNotas = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filtros.status !== 'todos') params.status = filtros.status;
      if (filtros.dataInicio) params.data_inicio = filtros.dataInicio;
      if (filtros.dataFim) params.data_fim = filtros.dataFim;

      const response = await api.get('/nfe/listar', { params });
      setNotas(response.data?.notas || []);
    } catch (error) {
      console.error('Erro ao carregar NF-e:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as notas fiscais",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [filtros, toast]);

  useEffect(() => {
    carregarNotas();
    carregarDiagnostico();
  }, [carregarNotas, carregarDiagnostico]);

  // Download XML individual
  const downloadXML = async (nota) => {
    try {
      const response = await api.get(`/nfe/${nota.id}/xml`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFe_${nota.numero}_${nota.chave?.substring(0, 20) || 'simulada'}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: "XML baixado com sucesso"
      });
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o XML",
        variant: "destructive"
      });
    }
  };

  // Consultar situação real da NF-e na SEFAZ pela chave de acesso
  const consultarSefaz = async (nota) => {
    if (!nota?.chave) {
      toast({ title: "Sem chave", description: "Esta nota não possui chave de acesso para consultar.", variant: "destructive" });
      return;
    }
    setConsultandoSefazId(nota.id);
    try {
      const response = await api.post('/nfe/consultar-chave', { chave: nota.chave });
      setConsultaSefazResult({ ...response.data, numero: nota.numero });
      setShowConsultaSefaz(true);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Não foi possível consultar a SEFAZ';
      toast({ title: "Erro na consulta", description: msg, variant: "destructive" });
    } finally {
      setConsultandoSefazId(null);
    }
  };


  // Download backup (ZIP)
  const downloadBackup = async () => {
    setProcessando(true);
    try {
      const params = {};
      if (filtros.status !== 'todos') params.status = filtros.status;
      if (filtros.dataInicio) params.data_inicio = filtros.dataInicio;
      if (filtros.dataFim) params.data_fim = filtros.dataFim;

      const response = await api.get('/nfe/backup/download', { 
        params,
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dataAtual = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `backup_nfe_${dataAtual}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: "Backup baixado com sucesso"
      });
    } catch (error) {
      console.error('Erro ao baixar backup:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível gerar o backup",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  // Excluir nota
  const excluirNota = async () => {
    if (!notaSelecionada) return;
    
    setProcessando(true);
    try {
      const response = await api.delete(`/nfe/${notaSelecionada.id}`);

      let mensagem = `NF-e ${notaSelecionada.numero} excluída com sucesso`;
      if (response.data?.numero_retornado) {
        mensagem += `. Número ${response.data.numero_excluido} devolvido à fila — será reutilizado na próxima emissão.`;
      }
      
      toast({
        title: "Sucesso",
        description: mensagem
      });
      
      setShowConfirmExcluir(false);
      setNotaSelecionada(null);
      carregarNotas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível excluir a NF-e",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  // Abrir modal para editar itens da NF-e (corrigir antes de transmitir SEFAZ)
  const abrirEditarItens = (nota) => {
    setNotaSelecionada(nota);
    // Clone profundo dos itens com fallback de nome→descricao
    const itens = (nota.itens || []).map((it) => ({
      descricao: it.descricao || it.nome || it.product_name || '',
      ncm: (it.ncm || '').toString().replace(/[^0-9]/g, ''),
      cfop: (it.cfop || '5102').toString(),
      cst: it.cst || '',
      csosn: it.csosn || '',
      cest: it.cest || '',
      ean: it.ean || it.codigo_barras || '',
      unidade: it.unidade || it.unidade_comercial || 'UN',
      quantidade: Number(it.quantidade || 1),
      valor_unitario: Number(it.valor_unitario || 0),
      // preserva quaisquer outros campos do item original
      ...it,
    }));
    setItensEditados(itens);
    setShowEditarItens(true);
  };

  const atualizarItemEditado = (index, campo, valor) => {
    setItensEditados((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [campo]: valor };
      // Recalcular valor_total se mudou qtd ou valor_unitario
      if (campo === 'quantidade' || campo === 'valor_unitario') {
        const q = Number(next[index].quantidade || 0);
        const v = Number(next[index].valor_unitario || 0);
        next[index].valor_total = +(q * v).toFixed(2);
      }
      return next;
    });
  };

  // Carregar XML de debug (mostra o XML completo que seria enviado à SEFAZ)
  const carregarDebugXml = async (nota) => {
    setNotaSelecionada(nota);
    setShowDebugXml(true);
    setDebugXmlLoading(true);
    setDebugXmlContent('');
    try {
      const res = await api.get(`/nfe/${nota.id}/debug-xml`);
      const xml = res?.data?.xml || '';
      const valid = res?.data?.validacao || {};
      const fsd = res?.data?.fiscal_settings_debug || {};
      let header = `🌍 Ambiente resolvido: ${res?.data?.ambiente || '?'} (tpAmb=${res?.data?.tpAmb || '?'})\n`;
      header += `🔑 Chave de acesso: ${res?.data?.chave_acesso || '(vazia!)'}\n`;
      header += `📏 Tamanho: ${res?.data?.tamanho_kb || '?'} KB\n`;
      header += `\n⚙️ Configuração fiscal (Configurações > Fiscal):\n`;
      header += `  • ambiente_nfe: ${fsd.ambiente_nfe ?? '(não definido)'}\n`;
      header += `  • ambiente (geral): ${fsd.ambiente ?? '(não definido)'}\n`;
      header += `  • certificado: ${fsd.tem_certificado ? 'OK' : 'NÃO CONFIGURADO'}\n`;
      header += `  • senha certificado: ${fsd.tem_senha_cert ? 'OK' : 'NÃO CONFIGURADA'}\n`;
      header += `\n🏢 CRT (Regime Tributário) — CRÍTICO para schema:\n`;
      header += `  • crt salvo (raw): ${fsd.crt_company_raw ?? '(não definido)'}\n`;
      header += `  • regime_tributario salvo (raw): ${fsd.regime_tributario_company_raw ?? '(não definido)'}\n`;
      header += `  • CRT que será usado no XML: ${fsd.crt_resolvido_xml ?? '?'} ${fsd.crt_resolvido_xml === 1 ? '(Simples Nacional → ICMSSN102)' : fsd.crt_resolvido_xml === 3 ? '(Regime Normal → ICMS00)' : ''}\n`;
      if (res?.data?.chave_acesso_erro) {
        header += `\n⚠️ Erro ao gerar chave: ${res.data.chave_acesso_erro}\n`;
      }
      if (valid.erros?.length) {
        header += `\n❌ Erros de validação interna:\n${valid.erros.map((e) => `  • ${e}`).join('\n')}\n`;
      }
      if (valid.avisos?.length) {
        header += `\n⚠️ Avisos:\n${valid.avisos.map((a) => `  • ${a}`).join('\n')}\n`;
      }
      // Resposta SEFAZ da última tentativa de emissão (se houver)
      if (res?.data?.tem_xml_real_transmitido) {
        header += `\n📡 ÚLTIMA TENTATIVA SEFAZ (XML realmente transmitido):\n`;
        header += `  • Status nota: ${res.data.status_nota || '?'}\n`;
        header += `  • Código SEFAZ: ${res.data.sefaz_codigo_ultima_tentativa || '(vazio)'}\n`;
        header += `  • Mensagem SEFAZ: ${res.data.sefaz_mensagem_ultima_tentativa || '(vazia)'}\n`;
        header += `  • Tamanho XML transmitido: ${res.data.tamanho_xml_real_kb} KB\n`;
        if (res?.data?.tem_sefaz_resposta_xml) {
          header += `\n--- RESPOSTA RAW DA SEFAZ (xMotivo, cStat, detalhes) ---\n\n${res.data.sefaz_resposta_xml}\n`;
          header += `\n--- =============================== ---\n`;
        }
        header += `\n--- XML REAL TRANSMITIDO À SEFAZ (última tentativa) ---\n\n${res.data.xml_real_transmitido}\n`;
        header += `\n=== =================== ===\n`;
      }
      header += '\n--- XML que SERÁ enviado se reenviar agora (REGENERADO) ---\n\n';
      setDebugXmlContent(header + xml);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erro desconhecido';
      setDebugXmlContent(`❌ Erro ao gerar XML de debug: ${msg}`);
    } finally {
      setDebugXmlLoading(false);
    }
  };

  // Abrir modal para editar cabeçalho (natureza op, destinatário, frete, info adicionais)
  const abrirEditarCabecalho = (nota) => {
    setNotaSelecionada(nota);
    const emit = nota.emitente || {};
    const ident = nota.identificacao || {};
    const dest = nota.destinatario || {};
    const dest_end = dest.endereco || {};
    const transp = nota.transporte || {};
    setCabecalhoEditado({
      natureza_operacao: emit.natureza_operacao || ident.natOp || 'VENDA',
      destinatario: {
        cpf_cnpj: dest.cpf_cnpj || dest.cnpj || dest.cpf || '',
        nome: dest.nome || '',
        ie: dest.ie || '',
        indIEDest: dest.indIEDest || '9',
        endereco: {
          logradouro: dest_end.logradouro || '',
          numero: dest_end.numero || '',
          complemento: dest_end.complemento || '',
          bairro: dest_end.bairro || '',
          cidade: dest_end.cidade || dest_end.municipio || '',
          cod_municipio: dest_end.cod_municipio || dest_end.codigo_municipio || '',
          uf: dest_end.uf || '',
          cep: dest_end.cep || '',
        }
      },
      frete: { modalidade: String(transp.modalidade ?? '9') },
      info_adicionais: nota.info_adicionais || ''
    });
    setShowEditarCabecalho(true);
  };

  const salvarCabecalho = async () => {
    if (!notaSelecionada) return;
    // Validação leve
    const problemas = [];
    if (!cabecalhoEditado.natureza_operacao?.trim()) problemas.push('Natureza da operação obrigatória');
    if (!cabecalhoEditado.destinatario.nome?.trim()) problemas.push('Nome do destinatário obrigatório');
    const doc = (cabecalhoEditado.destinatario.cpf_cnpj || '').replace(/\D/g, '');
    if (doc && doc.length !== 11 && doc.length !== 14) problemas.push('CPF/CNPJ inválido (use 11 ou 14 dígitos)');
    if (problemas.length) {
      toast({ title: 'Validação', description: problemas.join('\n'), variant: 'destructive' });
      return;
    }
    setSalvandoCabecalho(true);
    try {
      const payload = {
        natureza_operacao: cabecalhoEditado.natureza_operacao.trim(),
        destinatario: cabecalhoEditado.destinatario,
        frete: cabecalhoEditado.frete,
        info_adicionais: cabecalhoEditado.info_adicionais
      };
      await api.patch(`/nfe/${notaSelecionada.id}/cabecalho`, payload);
      toast({ title: 'Sucesso', description: 'Cabeçalho atualizado' });
      setShowEditarCabecalho(false);
      carregarNotas();
    } catch (e) {
      toast({
        title: 'Erro',
        description: e?.response?.data?.detail || 'Não foi possível atualizar o cabeçalho',
        variant: 'destructive'
      });
    } finally {
      setSalvandoCabecalho(false);
    }
  };


  const salvarEdicaoItens = async () => {
    if (!notaSelecionada) return;
    // Validação client-side leve
    const problemas = [];
    itensEditados.forEach((it, idx) => {
      if (!it.descricao?.trim()) problemas.push(`Item ${idx + 1}: descrição vazia`);
      const ncm = (it.ncm || '').toString().replace(/[^0-9]/g, '');
      if (!ncm || ncm === '00000000' || ncm.length !== 8) {
        problemas.push(`Item ${idx + 1}: NCM inválido (deve ter 8 dígitos)`);
      }
      const cfop = (it.cfop || '').toString().replace(/[^0-9]/g, '');
      if (cfop.length !== 4) problemas.push(`Item ${idx + 1}: CFOP inválido (4 dígitos)`);
    });
    if (problemas.length) {
      toast({
        title: '❌ Corrija antes de salvar',
        description: problemas.slice(0, 3).join(' • '),
        variant: 'destructive',
      });
      return;
    }
    setSalvandoEdicao(true);
    try {
      await api.patch(`/nfe/${notaSelecionada.id}/itens`, { itens: itensEditados });
      toast({
        title: '✅ Itens atualizados',
        description: 'Agora você pode enviar para a SEFAZ.',
      });
      setShowEditarItens(false);
      carregarNotas();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast({
        title: '❌ Erro ao salvar',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSalvandoEdicao(false);
    }
  };


  // Reenviar nota com pré-validação e tratamento de erro detalhado
  const reenviarNota = async (nota) => {
    setProcessandoId(nota.id);
    setErroReenvio(null);
    
    try {
      // PASSO 1: Validar a NF-e específica ANTES de enviar
      const validacaoResp = await api.post(`/nfe/${nota.id}/validar`);
      const validacao = validacaoResp.data;
      
      if (!validacao.pode_enviar) {
        // Montar mensagem de erro detalhada com campos faltantes
        const erros = validacao.erros || [];
        const errosPorCategoria = validacao.erros_por_categoria || {};
        
        const detalhes = [];
        
        // Erros de empresa
        if (errosPorCategoria.empresa?.length > 0) {
          detalhes.push('📋 **DADOS DA EMPRESA:**');
          errosPorCategoria.empresa.forEach(e => {
            detalhes.push(`   ⚠️ ${e.campo}: ${e.mensagem}`);
          });
        }
        
        // Erros de produtos
        if (errosPorCategoria.produtos?.length > 0) {
          detalhes.push('📦 **PRODUTOS:**');
          errosPorCategoria.produtos.forEach(e => {
            detalhes.push(`   ⚠️ ${e.campo}: ${e.mensagem}`);
          });
        }
        
        // Outros erros
        if (errosPorCategoria.outros?.length > 0) {
          errosPorCategoria.outros.forEach(e => {
            detalhes.push(`⚠️ ${e.campo}: ${e.mensagem}`);
          });
        }
        
        // Mostrar dados validados para referência
        const dadosValidados = validacao.dados_validados || {};
        const emitente = dadosValidados.emitente || {};
        
        setErroReenvio({
          tipo: 'validacao',
          titulo: `❌ ${validacao.total_erros} Campo(s) com Problema`,
          mensagem: 'A NF-e não pode ser enviada porque há campos obrigatórios faltando ou incorretos',
          emoji: '⚠️',
          proximoPasso: 'Corrija os campos indicados abaixo em Configurações > Empresa ou no Cadastro de Produtos',
          detalhes: detalhes,
          erros: erros,
          sugestoes: validacao.sugestoes || [],
          dadosAtuais: {
            cnpj: emitente.cnpj || '❌ Vazio',
            razaoSocial: emitente.razao_social || '❌ Vazio',
            logradouro: emitente.logradouro || '❌ Vazio',
            numero: emitente.numero || '❌ Vazio',
            cidade: emitente.cidade || '❌ Vazio',
            uf: emitente.uf || '❌ Vazio',
            codIbge: emitente.cod_ibge || '❌ Vazio',
            cep: emitente.cep || '❌ Vazio',
          },
          produtos: dadosValidados.produtos || []
        });
        setShowErroReenvio(true);
        setProcessandoId(null);
        return;
      }
      
      // PASSO 2: Validação OK - Enviar para SEFAZ
      const response = await api.post(`/nfe/${nota.id}/reenviar`);
      const data = response.data;

      // Notificar se houve re-sync de itens com o catálogo
      if (Array.isArray(data.resync_changes) && data.resync_changes.length > 0) {
        toast({
          title: `🔄 ${data.resync_changes.length} item(ns) atualizado(s) automaticamente`,
          description: data.resync_changes.slice(0, 3).join(' · ') + (data.resync_changes.length > 3 ? '…' : ''),
        });
      }

      if (data.success && data.autorizado) {
        // Sucesso total - nota autorizada
        toast({
          title: "✅ NF-e Autorizada!",
          description: `NF-e ${nota.numero} autorizada com sucesso pela SEFAZ (${data.ambiente?.toUpperCase() || 'PRODUÇÃO'})`,
        });
        carregarNotas();
        carregarDiagnostico();
      } else if (data.success && data.ja_autorizada) {
        toast({
          title: "ℹ️ Informação",
          description: "Esta NF-e já estava autorizada"
        });
      } else if (data.success && data.simulado) {
        // Ainda em simulação - mostrar detalhes do problema
        setErroReenvio({
          tipo: 'simulacao',
          titulo: '⚠️ NF-e ainda em Simulação',
          mensagem: data.message || data.motivo || 'Não foi possível enviar para SEFAZ',
          motivo: data.motivo,
          detalhes: [
            'A NF-e foi processada mas não foi enviada para a SEFAZ.',
            data.motivo || 'Verifique as configurações e tente novamente.'
          ]
        });
        setShowErroReenvio(true);
        carregarNotas();
      } else {
        // Erro explícito da API - usar interpretação SEFAZ se disponível
        const detalhes = [];
        const interpretacao = data.interpretacao;
        
        // Usar mensagem amigável se disponível
        if (interpretacao?.titulo) {
          detalhes.push(`❌ ${interpretacao.titulo}`);
        } else if (data.erro) {
          detalhes.push(`❌ ${data.erro}`);
        }
        
        if (interpretacao?.causa_provavel) {
          detalhes.push(`📋 Causa: ${interpretacao.causa_provavel}`);
        }
        
        if (interpretacao?.orientacao) {
          detalhes.push(`💡 Solução: ${interpretacao.orientacao}`);
        }
        
        if (interpretacao?.campos_verificar?.length > 0) {
          detalhes.push(`🔍 Verificar: ${interpretacao.campos_verificar.join(', ')}`);
        }

        // Mostrar o motivo EXATO da SEFAZ (xMotivo) — fundamental para debug
        if (data.erro_original) {
          detalhes.push(`📨 SEFAZ retornou: ${data.erro_original}`);
        }
        // RE-SYNC: o que foi atualizado automaticamente antes do envio
        if (Array.isArray(data.resync_changes) && data.resync_changes.length > 0) {
          detalhes.push(`\n🔄 Itens atualizados automaticamente do catálogo (antes do envio):\n${data.resync_changes.map((c) => `  • ${c}`).join('\n')}`);
        }
        // RESPOSTA RAW da SEFAZ (retEnviNFe completo) — para 225 sem detalhe
        if (data.xml_resposta_sefaz) {
          detalhes.push(`\n📡 RESPOSTA RAW DA SEFAZ:\n${data.xml_resposta_sefaz}`);
        }
        if (data.xml_enviado_preview) {
          detalhes.push(`\n📄 XML enviado (preview):\n${data.xml_enviado_preview}`);
        }

        if (data.erros_validacao) {
          detalhes.push(...data.erros_validacao.map(e => `⚠️ ${e}`));
        }
        if (data.problemas) {
          detalhes.push(...data.problemas.map(p => `⚠️ ${p}`));
        }
        if (data.avisos_validacao) {
          detalhes.push(...data.avisos_validacao.map(a => `ℹ️ ${a}`));
        }
        
        // Título baseado no código SEFAZ
        let titulo = '❌ Erro ao Reenviar NF-e';
        if (data.codigo_sefaz === '225') {
          titulo = '❌ Erro de Schema XML (225)';
        } else if (data.codigo_sefaz) {
          titulo = `❌ Rejeição SEFAZ (${data.codigo_sefaz})`;
        }
        
        setErroReenvio({
          tipo: 'erro',
          titulo: titulo,
          mensagem: interpretacao?.titulo || data.message || data.erro || 'Erro desconhecido',
          detalhes: detalhes.length > 0 ? detalhes : ['Verifique as configurações e tente novamente'],
          codigoSefaz: data.codigo_sefaz,
          interpretacao: interpretacao,
          sugestoes: interpretacao?.campos_verificar || []
        });
        setShowErroReenvio(true);
      }
    } catch (error) {
      console.error('Erro ao reenviar:', error);
      const errorData = error.response?.data;
      const detalhes = [];
      
      if (errorData?.detail) detalhes.push(`❌ ${errorData.detail}`);
      if (errorData?.erro) detalhes.push(`❌ ${errorData.erro}`);
      if (errorData?.erro_original) {
        detalhes.push(`📨 SEFAZ retornou: ${errorData.erro_original}`);
      }
      if (errorData?.erros_validacao) {
        detalhes.push(...errorData.erros_validacao.map(e => `⚠️ ${e}`));
      }
      if (errorData?.problemas) {
        detalhes.push(...errorData.problemas.map(p => `⚠️ ${p}`));
      }
      
      // Determinar título baseado no tipo de erro
      let titulo = '❌ Erro ao Enviar';
      let mensagem = 'Não foi possível comunicar com o servidor';
      
      if (errorData?.detail?.includes('certificado')) {
        titulo = '🔐 Problema no Certificado';
        mensagem = errorData.detail;
      } else if (errorData?.detail?.includes('empresa') || errorData?.detail?.includes('CNPJ')) {
        titulo = '🏢 Dados da Empresa Incompletos';
        mensagem = errorData.detail;
      } else if (errorData?.detail) {
        mensagem = errorData.detail;
      } else if (errorData?.message) {
        mensagem = errorData.message;
      } else if (errorData?.erro) {
        mensagem = errorData.erro;
      }
      
      setErroReenvio({
        tipo: 'erro',
        titulo: titulo,
        mensagem: mensagem,
        detalhes: detalhes.length > 0 ? detalhes : ['Verifique sua conexão e as configurações']
      });
      setShowErroReenvio(true);
    } finally {
      setProcessandoId(null);
    }
  };

  // Cancelar NF-e
  const cancelarNota = async () => {
    if (!notaSelecionada) return;
    
    if (!justificativaCancelamento || justificativaCancelamento.length < 15) {
      toast({
        title: "Erro",
        description: "Justificativa deve ter no mínimo 15 caracteres",
        variant: "destructive"
      });
      return;
    }
    
    setProcessando(true);
    try {
      const response = await api.post(`/nfe/${notaSelecionada.id}/cancelar`, {
        justificativa: justificativaCancelamento
      });
      
      toast({
        title: "Sucesso",
        description: response.data.mensagem || `NF-e ${notaSelecionada.numero} cancelada com sucesso`
      });
      
      setShowCancelar(false);
      setNotaSelecionada(null);
      setJustificativaCancelamento('');
      carregarNotas();
    } catch (error) {
      console.error('Erro ao cancelar:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível cancelar a NF-e",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  // Inutilizar numeração
  const inutilizarNumeracao = async () => {
    const { serie, numero_inicial, numero_final, justificativa } = inutilizarForm;
    
    if (!justificativa || justificativa.length < 15) {
      toast({
        title: "Erro",
        description: "Justificativa deve ter no mínimo 15 caracteres",
        variant: "destructive"
      });
      return;
    }
    
    if (!numero_inicial || !numero_final) {
      toast({
        title: "Erro",
        description: "Informe o número inicial e final",
        variant: "destructive"
      });
      return;
    }
    
    setProcessando(true);
    try {
      const response = await api.post('/nfe/inutilizar', {
        serie: parseInt(serie),
        numero_inicial: parseInt(numero_inicial),
        numero_final: parseInt(numero_final),
        justificativa
      });
      
      toast({
        title: "Sucesso",
        description: response.data.mensagem || `Numeração ${numero_inicial} a ${numero_final} inutilizada`
      });
      
      setShowInutilizar(false);
      setInutilizarForm({ serie: 1, numero_inicial: '', numero_final: '', justificativa: '' });
    } catch (error) {
      console.error('Erro ao inutilizar:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível inutilizar a numeração",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  // Carregar lista de inutilizações
  const carregarInutilizacoes = async () => {
    try {
      const response = await api.get('/nfe/inutilizacoes');
      setInutilizacoes(response.data.inutilizacoes || []);
    } catch (error) {
      console.error('Erro ao carregar inutilizações:', error);
    }
  };

  // Emitir Carta de Correção
  const emitirCartaCorrecao = async () => {
    if (!notaSelecionada) return;
    
    if (!textoCorrecao || textoCorrecao.length < 15) {
      toast({
        title: "Erro",
        description: "Texto da correção deve ter no mínimo 15 caracteres",
        variant: "destructive"
      });
      return;
    }
    
    if (textoCorrecao.length > 1000) {
      toast({
        title: "Erro",
        description: "Texto da correção não pode exceder 1000 caracteres",
        variant: "destructive"
      });
      return;
    }
    
    setProcessando(true);
    try {
      const response = await api.post(`/nfe/${notaSelecionada.id}/carta-correcao`, {
        correcao: textoCorrecao
      });
      
      toast({
        title: "Sucesso",
        description: response.data.mensagem || "Carta de Correção registrada com sucesso"
      });
      
      setShowCartaCorrecao(false);
      setTextoCorrecao('');
      carregarNotas();
    } catch (error) {
      console.error('Erro ao emitir CC-e:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível emitir a Carta de Correção",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  // Carregar cartas de correção de uma nota
  const carregarCartasCorrecao = async (nota) => {
    try {
      const response = await api.get(`/nfe/${nota.id}/cartas-correcao`);
      setCartasCorrecaoLista(response.data.cartas_correcao || []);
      setNotaSelecionada(nota);
      setShowListaCartas(true);
    } catch (error) {
      console.error('Erro ao carregar CC-e:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as Cartas de Correção",
        variant: "destructive"
      });
    }
  };

  // Imprimir DANFE
  const imprimirDANFE = async (nota) => {
    // Gerar HTML do DANFE para impressão
    const html = gerarHTMLDANFE(nota);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Gerar HTML do DANFE - Layout padrão brasileiro conforme modelo oficial
  const gerarHTMLDANFE = (nota) => {
    const formatarValorNum = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatarMoeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
    const formatarDataSimples = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '';
    const formatarHora = (d) => d ? new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
    const formatarChave = (chave) => chave ? chave.match(/.{1,4}/g)?.join(' ') : '';
    const isSimulado = nota.simulado !== false;
    
    // Dados do emitente
    const emit = nota.emitente || {};
    const emitEnd = emit.endereco || {};
    
    // Dados do destinatário
    const dest = nota.destinatario || {};
    const destEnd = dest.endereco || {};
    
    // Totais
    const tot = nota.totais || {};
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>DANFE - NF-e ${nota.numero}</title>
  <style>
    @page { size: A4; margin: 5mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: Arial, Helvetica, sans-serif; 
      font-size: 8pt; 
      margin: 0; 
      padding: 8px;
      background: #f0f0f0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .danfe-container { 
      max-width: 210mm; 
      margin: 0 auto; 
      background: #fff;
      position: relative;
    }
    
    /* Marca d'água SEM VALOR FISCAL */
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 60pt;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.08);
      white-space: nowrap;
      pointer-events: none;
      z-index: 100;
    }
    
    /* Estrutura de tabelas */
    table { border-collapse: collapse; width: 100%; }
    td, th { border: 1px solid #000; padding: 2px 4px; vertical-align: top; }
    
    /* Cabeçalho de Recebimento */
    .recibo { border: 1px solid #000; margin-bottom: 2px; }
    .recibo-header { 
      font-size: 6pt; 
      padding: 3px 5px; 
      border-bottom: 1px solid #000;
      line-height: 1.3;
    }
    .recibo-row { display: flex; }
    .recibo-cell { 
      border-right: 1px solid #000; 
      padding: 3px 5px;
      font-size: 7pt;
    }
    .recibo-cell:last-child { border-right: none; }
    .recibo-cell label { display: block; font-size: 6pt; color: #333; }
    
    /* Seção Principal */
    .main-section { border: 1px solid #000; }
    
    /* Cabeçalho com Emitente + DANFE + Código de Barras */
    .header-row { display: flex; border-bottom: 1px solid #000; }
    
    .emitente-col {
      width: 42%;
      border-right: 1px solid #000;
      padding: 5px;
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    .emitente-logo {
      width: 60px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ccc;
      font-size: 10pt;
      font-weight: bold;
      color: #007bff;
    }
    .emitente-dados {
      flex: 1;
      font-size: 7pt;
      line-height: 1.4;
    }
    .emitente-nome {
      font-size: 10pt;
      font-weight: bold;
      margin-bottom: 3px;
    }
    
    .danfe-col {
      width: 18%;
      border-right: 1px solid #000;
      text-align: center;
      padding: 5px;
    }
    .danfe-title {
      font-size: 18pt;
      font-weight: bold;
      margin-bottom: 2px;
    }
    .danfe-subtitle {
      font-size: 6pt;
      margin-bottom: 5px;
      line-height: 1.2;
    }
    .danfe-tipo-row {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      font-size: 7pt;
      margin-bottom: 5px;
    }
    .danfe-tipo-box {
      border: 1px solid #000;
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
    }
    .danfe-info { font-size: 8pt; line-height: 1.4; }
    .danfe-info strong { font-size: 9pt; }
    
    .barcode-col {
      width: 40%;
      padding: 5px;
    }
    .barcode-placeholder {
      height: 40px;
      background: linear-gradient(90deg, #000 2px, transparent 2px) 0 0 / 4px 100%;
      margin-bottom: 5px;
    }
    .chave-acesso { font-size: 7pt; margin-bottom: 5px; }
    .chave-acesso label { font-weight: bold; display: block; margin-bottom: 2px; }
    .chave-acesso span { font-family: 'Courier New', monospace; font-size: 8pt; }
    .consulta-info { font-size: 6pt; color: #333; margin-bottom: 5px; line-height: 1.3; }
    
    /* Linha Natureza / Protocolo */
    .natureza-protocolo { display: flex; border-bottom: 1px solid #000; }
    .natureza-cell {
      flex: 1;
      border-right: 1px solid #000;
      padding: 3px 5px;
    }
    .natureza-cell:last-child { border-right: none; }
    .natureza-cell label { display: block; font-size: 6pt; color: #333; }
    .natureza-cell span { font-size: 8pt; }
    
    /* Linha IE */
    .ie-row { display: flex; border-bottom: 1px solid #000; }
    .ie-cell {
      flex: 1;
      border-right: 1px solid #000;
      padding: 3px 5px;
    }
    .ie-cell:last-child { border-right: none; }
    .ie-cell label { display: block; font-size: 6pt; color: #333; }
    .ie-cell span { font-size: 8pt; }
    
    /* Seção Títulos */
    .section-title {
      background: #e0e0e0;
      font-weight: bold;
      font-size: 7pt;
      padding: 2px 5px;
      border-bottom: 1px solid #000;
    }
    
    /* Destinatário */
    .dest-row { display: flex; border-bottom: 1px solid #000; }
    .dest-cell {
      border-right: 1px solid #000;
      padding: 2px 4px;
    }
    .dest-cell:last-child { border-right: none; }
    .dest-cell label { display: block; font-size: 5pt; color: #333; text-transform: uppercase; }
    .dest-cell span { font-size: 7pt; display: block; min-height: 10px; }
    
    /* Cálculo do Imposto */
    .imposto-grid { display: flex; flex-wrap: wrap; border-bottom: 1px solid #000; }
    .imposto-cell {
      flex: 1;
      min-width: 11%;
      border-right: 1px solid #000;
      padding: 2px 4px;
      text-align: right;
    }
    .imposto-cell:last-child { border-right: none; }
    .imposto-cell label { display: block; font-size: 5pt; color: #333; text-align: left; }
    .imposto-cell span { font-size: 8pt; display: block; }
    
    /* Transportador */
    .transp-row { display: flex; border-bottom: 1px solid #000; }
    .transp-cell {
      border-right: 1px solid #000;
      padding: 2px 4px;
    }
    .transp-cell:last-child { border-right: none; }
    .transp-cell label { display: block; font-size: 5pt; color: #333; }
    .transp-cell span { font-size: 7pt; display: block; min-height: 10px; }
    
    /* Tabela de Produtos */
    .produtos-table { width: 100%; border-collapse: collapse; }
    .produtos-table th {
      background: #f0f0f0;
      font-size: 5pt;
      font-weight: bold;
      padding: 2px;
      text-align: center;
      border: 1px solid #000;
      text-transform: uppercase;
    }
    .produtos-table td {
      font-size: 6pt;
      padding: 2px 3px;
      border: 1px solid #000;
      vertical-align: top;
    }
    .produtos-table td.cod { text-align: center; }
    .produtos-table td.desc { text-align: left; }
    .produtos-table td.num { text-align: right; }
    .produtos-table td.center { text-align: center; }
    
    /* Dados Adicionais */
    .dados-adicionais { display: flex; border-top: 1px solid #000; }
    .info-complementares {
      flex: 1;
      border-right: 1px solid #000;
      padding: 3px 5px;
      min-height: 60px;
    }
    .reservado-fisco {
      width: 35%;
      padding: 3px 5px;
      min-height: 60px;
    }
    .dados-adicionais label { display: block; font-size: 6pt; font-weight: bold; margin-bottom: 3px; }
    .dados-adicionais p { font-size: 6pt; line-height: 1.4; }
    
    /* Utilitários */
    .text-center { text-align: center !important; }
    .text-right { text-align: right !important; }
    .font-bold { font-weight: bold; }
    
    @media print { 
      body { padding: 0; background: #fff; }
      .danfe-container { box-shadow: none; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="danfe-container">
    ${isSimulado ? '<div class="watermark">SEM VALOR FISCAL</div>' : ''}
    
    <!-- CANHOTO / RECIBO -->
    <div class="recibo">
      <div class="recibo-header">
        RECEBEMOS DE ${emit.razao_social || 'EMITENTE'} OS PRODUTOS/SERVIÇOS CONSTANTES NA NOTA FISCAL INDICADA AO LADO. EMISSÃO: ${formatarDataSimples(nota.created_at)}
        <span style="float: right;">VALOR DA NOTA: ${formatarMoeda(tot.valor_total)}</span>
      </div>
      <div class="recibo-row">
        <div class="recibo-cell" style="width: 15%;">
          <label>DATA DE RECEBIMENTO</label>
          <span>____/____/____</span>
        </div>
        <div class="recibo-cell" style="width: 35%;">
          <label>IDENTIFICAÇÃO E ASSINATURA DO RECEBEDOR</label>
          <span></span>
        </div>
        <div class="recibo-cell" style="width: 35%;">
          <label>DESTINATÁRIO</label>
          <span>${dest.nome || 'CONSUMIDOR'}</span>
        </div>
        <div class="recibo-cell" style="width: 15%; text-align: center;">
          <strong style="font-size: 12pt;">NF-e</strong><br>
          <span>N°: ${String(nota.numero).padStart(9, '0')}</span><br>
          <span>SÉRIE: ${nota.serie || 1}</span>
        </div>
      </div>
    </div>
    
    <!-- CORPO PRINCIPAL DA DANFE -->
    <div class="main-section">
      
      <!-- CABEÇALHO: EMITENTE | DANFE | CÓDIGO DE BARRAS -->
      <div class="header-row">
        <!-- Emitente -->
        <div class="emitente-col">
          <div class="emitente-logo">▶</div>
          <div class="emitente-dados">
            <div class="emitente-nome">${emit.razao_social || 'EMPRESA EMITENTE'}</div>
            ${emitEnd.logradouro || ''}, ${emitEnd.numero || 'S/N'}${emitEnd.complemento ? ' ' + emitEnd.complemento : ''}<br>
            ${emitEnd.bairro || ''}<br>
            ${emitEnd.cidade || ''} - ${emitEnd.uf || ''}<br>
            CEP: ${emitEnd.cep || ''}<br>
            TELEFONE: ${emit.fone || ''}<br>
            E-MAIL: ${emit.email || ''}
          </div>
        </div>
        
        <!-- DANFE -->
        <div class="danfe-col">
          <div class="danfe-title">DANFE</div>
          <div class="danfe-subtitle">DOCUMENTO AUXILIAR<br>DA NOTA FISCAL<br>ELETRÔNICA</div>
          <div class="danfe-tipo-row">
            <span>0 - Entrada</span>
            <div class="danfe-tipo-box">${nota.tipo === 0 ? '0' : ''}</div>
          </div>
          <div class="danfe-tipo-row">
            <span>1 - Saída</span>
            <div class="danfe-tipo-box">${nota.tipo !== 0 ? '1' : ''}</div>
          </div>
          <div class="danfe-info">
            N° <strong>${String(nota.numero).padStart(9, '0')}</strong><br>
            SÉRIE: <strong>${nota.serie || 1}</strong><br>
            FOLHA: 1 de 1
          </div>
        </div>
        
        <!-- Código de Barras e Chave -->
        <div class="barcode-col">
          <div class="barcode-placeholder"></div>
          <div class="chave-acesso">
            <label>CHAVE DE ACESSO</label>
            <span>${formatarChave(nota.chave) || 'SIMULAÇÃO - SEM CHAVE DE ACESSO'}</span>
          </div>
          <div class="consulta-info">
            Consulta de autenticidade no portal nacional da NF-e<br>
            www.nfe.fazenda.gov.br/portal ou no site da SEFAZ Autorizadora
          </div>
          <div class="chave-acesso">
            <label>PROTOCOLO DE AUTORIZAÇÃO DE USO</label>
            <span>${nota.protocolo || 'SIMULAÇÃO'} - ${formatarDataSimples(nota.data_autorizacao || nota.created_at)} ${formatarHora(nota.data_autorizacao || nota.created_at)}</span>
          </div>
        </div>
      </div>
      
      <!-- NATUREZA DA OPERAÇÃO / PROTOCOLO -->
      <div class="natureza-protocolo">
        <div class="natureza-cell" style="flex: 2;">
          <label>NATUREZA DA OPERAÇÃO</label>
          <span>${nota.natureza_operacao || 'VENDA MERC.ADQ.REC.TERC'}</span>
        </div>
        <div class="natureza-cell">
          <label>PROTOCOLO DE AUTORIZAÇÃO DE USO</label>
          <span>${nota.protocolo || 'SIMULAÇÃO'} - ${formatarDataSimples(nota.data_autorizacao || nota.created_at)} ${formatarHora(nota.data_autorizacao || nota.created_at)}</span>
        </div>
      </div>
      
      <!-- INSCRIÇÃO ESTADUAL -->
      <div class="ie-row">
        <div class="ie-cell">
          <label>INSCRIÇÃO ESTADUAL</label>
          <span>${emit.ie || ''}</span>
        </div>
        <div class="ie-cell">
          <label>INSCRIÇÃO ESTADUAL SUB. TRIBUTÁRIA</label>
          <span></span>
        </div>
        <div class="ie-cell">
          <label>CNPJ</label>
          <span>${emit.cnpj || ''}</span>
        </div>
      </div>
      
      <!-- DESTINATÁRIO/REMETENTE -->
      <div class="section-title">DESTINATÁRIO/REMETENTE</div>
      <div class="dest-row">
        <div class="dest-cell" style="flex: 3;">
          <label>NOME/RAZÃO SOCIAL</label>
          <span>${dest.nome || '-'}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>CNPJ/CPF</label>
          <span>${dest.cpf_cnpj || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>DATA DA EMISSÃO</label>
          <span>${formatarDataSimples(nota.created_at)}</span>
        </div>
      </div>
      <div class="dest-row">
        <div class="dest-cell" style="flex: 3;">
          <label>ENDEREÇO</label>
          <span>${destEnd.logradouro || ''} ${destEnd.numero || ''} ${destEnd.complemento || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>BAIRRO/DISTRITO</label>
          <span>${destEnd.bairro || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>CEP</label>
          <span>${destEnd.cep || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>DATA DE SAÍDA/ENTRADA</label>
          <span>${formatarDataSimples(nota.data_saida || nota.created_at)}</span>
        </div>
      </div>
      <div class="dest-row">
        <div class="dest-cell" style="flex: 2;">
          <label>MUNICÍPIO</label>
          <span>${destEnd.cidade || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>FONE/FAX</label>
          <span>${dest.telefone || ''}</span>
        </div>
        <div class="dest-cell" style="width: 50px;">
          <label>UF</label>
          <span>${destEnd.uf || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>INSCRIÇÃO ESTADUAL</label>
          <span>${dest.ie || ''}</span>
        </div>
        <div class="dest-cell" style="flex: 1;">
          <label>HORA DE SAÍDA</label>
          <span>${formatarHora(nota.data_saida || nota.created_at)}</span>
        </div>
      </div>
      
      <!-- FATURA -->
      <div class="section-title">FATURA</div>
      <div class="dest-row" style="min-height: 20px;">
        <div class="dest-cell" style="flex: 1;"></div>
      </div>
      
      <!-- CÁLCULO DO IMPOSTO -->
      <div class="section-title">CÁLCULO DO IMPOSTO</div>
      <div class="imposto-grid">
        <div class="imposto-cell">
          <label>BASE DE CÁLCULO DE ICMS</label>
          <span>${formatarValorNum(tot.base_icms || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR DO ICMS</label>
          <span>${formatarValorNum(tot.valor_icms || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>BASE DE CÁLCULO ICMS ST</label>
          <span>${formatarValorNum(tot.base_icms_st || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR DO ICMS SUBSTITUIÇÃO</label>
          <span>${formatarValorNum(tot.valor_icms_st || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR TOTAL DOS PRODUTOS</label>
          <span>${formatarValorNum(tot.valor_produtos || tot.valor_total)}</span>
        </div>
      </div>
      <div class="imposto-grid">
        <div class="imposto-cell">
          <label>VALOR DO FRETE</label>
          <span>${formatarValorNum(tot.valor_frete || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR DO SEGURO</label>
          <span>${formatarValorNum(tot.valor_seguro || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>DESCONTO</label>
          <span>${formatarValorNum(tot.valor_desconto || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>OUTRAS DESPESAS ACESSÓRIAS</label>
          <span>${formatarValorNum(tot.outras_despesas || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR DO IPI</label>
          <span>${formatarValorNum(tot.valor_ipi || 0)}</span>
        </div>
        <div class="imposto-cell">
          <label>VALOR APROX. DOS TRIBUTOS</label>
          <span>${formatarValorNum(tot.valor_tributos || 0)}</span>
        </div>
        <div class="imposto-cell" style="background: #f5f5f5;">
          <label>VALOR TOTAL DA NOTA</label>
          <span style="font-weight: bold; font-size: 10pt;">${formatarValorNum(tot.valor_total)}</span>
        </div>
      </div>
      
      <!-- TRANSPORTADOR/VOLUMES TRANSPORTADOS -->
      <div class="section-title">TRANSPORTADOR/VOLUMES TRANSPORTADOS</div>
      <div class="transp-row">
        <div class="transp-cell" style="flex: 2;">
          <label>RAZÃO SOCIAL</label>
          <span>${nota.transportador?.nome || ''}</span>
        </div>
        <div class="transp-cell" style="width: 100px;">
          <label>FRETE POR CONTA</label>
          <span>9 - Sem Frete</span>
        </div>
        <div class="transp-cell" style="width: 80px;">
          <label>CÓDIGO ANTT</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 80px;">
          <label>PLACA DO VEÍC.</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 30px;">
          <label>UF</label>
          <span></span>
        </div>
        <div class="transp-cell" style="flex: 1;">
          <label>CNPJ/CPF</label>
          <span></span>
        </div>
      </div>
      <div class="transp-row">
        <div class="transp-cell" style="flex: 2;">
          <label>ENDEREÇO</label>
          <span></span>
        </div>
        <div class="transp-cell" style="flex: 1;">
          <label>MUNICÍPIO</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 30px;">
          <label>UF</label>
          <span></span>
        </div>
        <div class="transp-cell" style="flex: 1;">
          <label>INSCRIÇÃO ESTADUAL</label>
          <span></span>
        </div>
      </div>
      <div class="transp-row">
        <div class="transp-cell" style="width: 80px;">
          <label>QUANTIDADE</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 80px;">
          <label>ESPÉCIE</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 80px;">
          <label>MARCA</label>
          <span></span>
        </div>
        <div class="transp-cell" style="flex: 1;">
          <label>NUMERAÇÃO</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 100px;">
          <label>PESO BRUTO</label>
          <span></span>
        </div>
        <div class="transp-cell" style="width: 100px;">
          <label>PESO LÍQUIDO</label>
          <span></span>
        </div>
      </div>
      
      <!-- DADOS DO PRODUTO/SERVIÇO -->
      <div class="section-title">DADOS DO PRODUTO/SERVIÇO</div>
      <table class="produtos-table">
        <thead>
          <tr>
            <th style="width: 6%;">CÓD. PROD.</th>
            <th style="width: 26%;">DESCRIÇÃO DO PRODUTO/SERVIÇO</th>
            <th style="width: 8%;">NCM/SH</th>
            <th style="width: 3%;">O/CST</th>
            <th style="width: 5%;">CFOP</th>
            <th style="width: 4%;">UNID.</th>
            <th style="width: 6%;">QUANT.</th>
            <th style="width: 8%;">VALOR UNITÁRIO</th>
            <th style="width: 8%;">VALOR TOTAL</th>
            <th style="width: 6%;">B.C. ICMS</th>
            <th style="width: 6%;">VALOR ICMS</th>
            <th style="width: 6%;">VALOR IPI</th>
            <th style="width: 4%;">ALÍQ. ICMS</th>
            <th style="width: 4%;">ALÍQ. IPI</th>
          </tr>
        </thead>
        <tbody>
          ${(nota.itens || []).map((item, idx) => `
            <tr>
              <td class="cod">${item.codigo || String(idx + 1).padStart(3, '0')}</td>
              <td class="desc">${item.descricao || '-'}${item.informacoes_adicionais ? '<br><small>' + item.informacoes_adicionais + '</small>' : ''}</td>
              <td class="center">${item.ncm || ''}</td>
              <td class="center">${item.cst || '0'}</td>
              <td class="center">${item.cfop || '5102'}</td>
              <td class="center">${item.unidade || 'UN'}</td>
              <td class="num">${formatarValorNum(item.quantidade || 1)}</td>
              <td class="num">${formatarValorNum(item.valor_unitario)}</td>
              <td class="num">${formatarValorNum(item.valor_total)}</td>
              <td class="num">${formatarValorNum(item.base_icms || 0)}</td>
              <td class="num">${formatarValorNum(item.valor_icms || 0)}</td>
              <td class="num">${formatarValorNum(item.valor_ipi || 0)}</td>
              <td class="num">${formatarValorNum(item.aliq_icms || 0)}</td>
              <td class="num">${formatarValorNum(item.aliq_ipi || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- DADOS ADICIONAIS -->
      <div class="section-title">DADOS ADICIONAIS</div>
      <div class="dados-adicionais">
        <div class="info-complementares">
          <label>INFORMAÇÕES COMPLEMENTARES</label>
          <p>${nota.informacoes_adicionais || ''}</p>
        </div>
        <div class="reservado-fisco">
          <label>RESERVADO AO FISCO</label>
          <p></p>
        </div>
      </div>
      
    </div>
  </div>
</body>
</html>`;
  };

  // Formatadores
  const formatarData = (dataStr) => {
    if (!dataStr) return '-';
    try {
      return new Date(dataStr).toLocaleString('pt-BR');
    } catch {
      return dataStr;
    }
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  // Componente de ícone de status
  const StatusIcon = ({ nota }) => {
    const isSimulado = nota.simulado !== false;
    const status = nota.status || 'simulada';
    
    if (status === 'autorizada' && !isSimulado) {
      return (
        <div className="flex items-center gap-1" title="Autorizada pela SEFAZ">
          <ShieldCheck className="h-5 w-5 text-green-500" />
        </div>
      );
    } else if (status === 'erro') {
      return (
        <div className="flex items-center gap-1" title={nota.nfe_erro || 'Erro na emissão'}>
          <ShieldX className="h-5 w-5 text-red-500" />
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1" title="Simulação - não enviada para SEFAZ">
          <ShieldAlert className="h-5 w-5 text-yellow-500" />
        </div>
      );
    }
  };

  // Badge de status melhorado
  const getStatusBadge = (nota) => {
    const isSimulado = nota.simulado !== false;
    const status = nota.status || 'simulada';
    
    if (status === 'cancelada') {
      return (
        <Badge className="bg-gray-500 hover:bg-gray-600 gap-1">
          <Ban className="h-3 w-3" />
          Cancelada
        </Badge>
      );
    } else if (status === 'autorizada' && !isSimulado) {
      return (
        <Badge className="bg-green-500 hover:bg-green-600 gap-1">
          <Check className="h-3 w-3" />
          Autorizada
        </Badge>
      );
    } else if (status === 'erro') {
      return (
        <Badge className="bg-red-500 hover:bg-red-600 gap-1">
          <X className="h-3 w-3" />
          Erro
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-yellow-500 hover:bg-yellow-600 gap-1">
          <AlertTriangle className="h-3 w-3" />
          Simulada
        </Badge>
      );
    }
  };

  // Estatísticas
  const stats = {
    total: notas.length,
    autorizadas: notas.filter(n => n.status === 'autorizada' && n.simulado === false).length,
    simuladas: notas.filter(n => n.simulado !== false || n.status === 'simulada').length,
    erros: notas.filter(n => n.status === 'erro').length,
    valorTotal: notas.reduce((acc, n) => acc + (n.totais?.valor_total || 0), 0)
  };

  // Verificar se pode emitir em modo real
  const podeEmitirReal = diagnostico?.pronto_para_emitir;
  const problemasDiagnostico = diagnostico?.problemas || [];
  const avisosDiagnostico = diagnostico?.avisos || [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gerenciar NF-e</h1>
          <p className="text-muted-foreground">Consulte, exporte e gerencie suas Notas Fiscais Eletrônicas</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.location.href = '/emitir-nfe'} className="gap-2">
            <FileText className="h-4 w-4" />
            Emitir Nova NF-e
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowInutilizar(true)}
            className="gap-2"
          >
            <FileX className="h-4 w-4" />
            Inutilizar Numeração
          </Button>
        </div>
      </div>

      {/* Card de Diagnóstico Completo */}
      {diagnostico && (
        <Card className={podeEmitirReal ? 'border-green-500 bg-green-50/50' : (problemasDiagnostico.length > 0 ? 'border-red-500 bg-red-50/50' : 'border-yellow-500 bg-yellow-50/50')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                {podeEmitirReal ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-green-700">Pronto para Emissão Real</span>
                  </>
                ) : problemasDiagnostico.length > 0 ? (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-700">{problemasDiagnostico.length} Problema(s) a Resolver</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <span className="text-yellow-700">Modo Simulação Ativo</span>
                  </>
                )}
              </div>
              <Badge variant={diagnostico?.ambiente?.atual === 'producao' ? 'destructive' : 'secondary'}>
                {diagnostico?.ambiente?.atual === 'producao' ? '🔴 PRODUÇÃO' : '🟡 HOMOLOGAÇÃO'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status das Configurações */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-2 rounded bg-white/60 flex items-center gap-2">
                {diagnostico?.certificado?.tem_arquivo ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
                <span>Certificado Digital</span>
              </div>
              <div className="p-2 rounded bg-white/60 flex items-center gap-2">
                {diagnostico?.certificado?.tem_senha ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
                <span>Senha Certificado</span>
              </div>
              <div className="p-2 rounded bg-white/60 flex items-center gap-2">
                {diagnostico?.empresa?.cnpj && diagnostico?.empresa?.cnpj !== 'NÃO CONFIGURADO' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
                <span className="truncate" title={diagnostico?.empresa?.cnpj}>
                  CNPJ: {diagnostico?.empresa?.cnpj && diagnostico?.empresa?.cnpj !== 'NÃO CONFIGURADO' ? diagnostico?.empresa?.cnpj?.substring(0, 10) + '...' : 'Pendente'}
                </span>
              </div>
              <div className="p-2 rounded bg-white/60 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-500" />
                <span>CRT: {diagnostico?.empresa?.crt_descricao || 'N/A'}</span>
              </div>
            </div>

            {/* Informações da Empresa */}
            <div className="p-3 rounded bg-white/60 text-sm">
              <p className="font-medium mb-2">Dados da Empresa (usados na NF-e):</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-slate-600">
                <p>Razão Social: <strong>{diagnostico?.empresa?.razao_social && diagnostico?.empresa?.razao_social !== 'NÃO CONFIGURADO' ? diagnostico?.empresa?.razao_social : 'Não configurado'}</strong></p>
                <p>IE: <strong>{diagnostico?.empresa?.ie && diagnostico?.empresa?.ie !== 'NÃO CONFIGURADO' ? diagnostico?.empresa?.ie : 'ISENTO'}</strong></p>
                <p>Endereço: <strong>{diagnostico?.endereco?.logradouro && diagnostico?.endereco?.logradouro !== 'NÃO CONFIGURADO' ? diagnostico?.endereco?.logradouro : 'Não configurado'}</strong></p>
                <p>Cidade/UF: <strong>{diagnostico?.endereco?.cidade && diagnostico?.endereco?.cidade !== 'NÃO CONFIGURADO' ? diagnostico?.endereco?.cidade : 'N/A'}/{diagnostico?.endereco?.uf && diagnostico?.endereco?.uf !== 'NÃO CONFIGURADO' ? diagnostico?.endereco?.uf : 'N/A'}</strong></p>
                <p>Cód. IBGE: <strong>{diagnostico?.endereco?.cod_municipio && diagnostico?.endereco?.cod_municipio !== 'NÃO CONFIGURADO' ? diagnostico?.endereco?.cod_municipio : 'Não configurado'}</strong></p>
                <p>Próxima NF-e: <strong>Nº {diagnostico?.numeracao?.proximo_numero || 1} - Série {diagnostico?.numeracao?.serie || 1}</strong></p>
              </div>
            </div>

            {/* Lista de Problemas */}
            {problemasDiagnostico.length > 0 && (
              <div className="p-3 rounded bg-red-100 border border-red-300">
                <p className="font-medium text-sm mb-2 flex items-center gap-2 text-red-700">
                  <XCircle className="h-4 w-4" />
                  Problemas que impedem emissão real:
                </p>
                <ul className="space-y-1 text-sm text-red-600">
                  {problemasDiagnostico.map((problema, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>
                        {typeof problema === 'string' ? problema : (
                          <>
                            <strong>{problema.campo}:</strong> {problema.mensagem}
                            {problema.onde_configurar && (
                              <span className="text-red-500"> → {problema.onde_configurar}</span>
                            )}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mt-3 text-red-700 border-red-400"
                  onClick={() => window.location.href = '/settings'}
                >
                  Ir para Configurações →
                </Button>
              </div>
            )}

            {/* Lista de Avisos */}
            {avisosDiagnostico.length > 0 && (
              <div className="p-3 rounded bg-yellow-100 border border-yellow-300">
                <p className="font-medium text-sm mb-2 flex items-center gap-2 text-yellow-700">
                  <AlertTriangle className="h-4 w-4" />
                  Avisos (não impedem emissão):
                </p>
                <ul className="space-y-1 text-sm text-yellow-600">
                  {avisosDiagnostico.map((aviso, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-yellow-400">•</span>
                      <span>
                        <strong>{aviso.campo}:</strong> {aviso.mensagem}
                        {aviso.impacto && (
                          <span className="text-yellow-500"> → {aviso.impacto}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filtros e Ações */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Status</Label>
              <Select value={filtros.status} onValueChange={(v) => setFiltros({...filtros, status: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="autorizada">Autorizadas</SelectItem>
                  <SelectItem value="simulada">Simuladas</SelectItem>
                  <SelectItem value="erro">Com Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label>Data Início</Label>
              <Input 
                type="date" 
                value={filtros.dataInicio}
                onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label>Data Fim</Label>
              <Input 
                type="date" 
                value={filtros.dataFim}
                onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
              />
            </div>
            <Button onClick={carregarNotas} disabled={loading} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Buscar
            </Button>
            <Button onClick={downloadBackup} disabled={processando || notas.length === 0} variant="outline" className="gap-2">
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
              Exportar ZIP
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-500">{stats.autorizadas}</div>
            <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <ShieldCheck className="h-4 w-4 text-green-500" />
              Autorizadas
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-yellow-500">{stats.simuladas}</div>
            <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <ShieldAlert className="h-4 w-4 text-yellow-500" />
              Simuladas
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-red-500">{stats.erros}</div>
            <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <ShieldX className="h-4 w-4 text-red-500" />
              Erros
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{formatarValor(stats.valorTotal)}</div>
            <div className="text-sm text-muted-foreground">Valor Total</div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Notas */}
      <Card>
        <CardHeader>
          <CardTitle>Notas Fiscais Eletrônicas</CardTitle>
          <CardDescription>
            {notas.length} nota(s) encontrada(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : notas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma NF-e encontrada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notas.map((nota) => (
                <div 
                  key={nota.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Ícone de Status */}
                    <StatusIcon nota={nota} />
                    
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">NF-e #{nota.numero}</span>
                        <span className="text-sm text-muted-foreground">Série {nota.serie || 1}</span>
                        {getStatusBadge(nota)}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatarData(nota.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {nota.destinatario?.nome?.substring(0, 30) || 'Consumidor'}
                        </span>
                        <span className="font-medium">
                          {formatarValor(nota.totais?.valor_total)}
                        </span>
                      </div>
                      {nota.chave && (
                        <div className="text-xs text-muted-foreground font-mono mt-1">
                          {nota.chave}
                        </div>
                      )}
                      {/* Mostrar erro se houver */}
                      {nota.nfe_erro && (
                        <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {nota.nfe_erro}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Botão Ver Detalhes */}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setNotaSelecionada(nota); setShowDetalhes(true); }}
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    
                    {/* Botão Imprimir DANFE */}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => imprimirDANFE(nota)}
                      title="Imprimir DANFE"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    
                    {/* Botão Download XML */}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => downloadXML(nota)}
                      title="Baixar XML"
                    >
                      <Download className="h-4 w-4" />
                    </Button>

                    {/* Botão Consultar SEFAZ pela chave */}
                    {nota.chave && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => consultarSefaz(nota)}
                        disabled={consultandoSefazId === nota.id}
                        title="Consultar situação na SEFAZ (pela chave)"
                        data-testid={`consultar-sefaz-${nota.id}`}
                      >
                        {consultandoSefazId === nota.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    )}
                    
                    {/* Botões para notas simuladas ou com erro */}
                    {(nota.simulado !== false || nota.status === 'simulada' || nota.status === 'erro') && nota.status !== 'cancelada' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="gap-1"
                        onClick={() => reenviarNota(nota)}
                        disabled={processandoId === nota.id}
                        title="Reenviar para SEFAZ"
                      >
                        {processandoId === nota.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">Enviar SEFAZ</span>
                      </Button>
                    )}
                    
                    {/* Menu de Ações */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Ações</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        {/* Editar Itens — só para notas NÃO autorizadas/canceladas (permite corrigir antes de enviar) */}
                        {nota.status !== 'autorizada' && nota.status !== 'cancelada' && (
                          <DropdownMenuItem
                            onClick={() => abrirEditarItens(nota)}
                            data-testid={`menu-editar-itens-${nota.id}`}
                          >
                            <Edit3 className="h-4 w-4 mr-2" />
                            Editar Itens (NCM/CFOP/Descrição)
                          </DropdownMenuItem>
                        )}

                        {/* Editar Cabeçalho — natureza op, destinatário, frete, info adicionais */}
                        {nota.status !== 'autorizada' && nota.status !== 'cancelada' && (
                          <DropdownMenuItem
                            onClick={() => abrirEditarCabecalho(nota)}
                            data-testid={`menu-editar-cabecalho-${nota.id}`}
                          >
                            <Edit3 className="h-4 w-4 mr-2" />
                            Editar Cabeçalho (Destinatário/Frete)
                          </DropdownMenuItem>
                        )}

                        {/* Debug XML — gera o XML que seria enviado à SEFAZ (sem transmitir) */}
                        <DropdownMenuItem
                          onClick={() => carregarDebugXml(nota)}
                          data-testid={`menu-debug-xml-${nota.id}`}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          🔍 Debug XML (ver o que vai para SEFAZ)
                        </DropdownMenuItem>

                        {/* Carta de Correção - apenas para notas autorizadas ou simuladas não canceladas */}
                        {nota.status !== 'cancelada' && (
                          <DropdownMenuItem 
                            onClick={() => { setNotaSelecionada(nota); setShowCartaCorrecao(true); }}
                          >
                            <Edit3 className="h-4 w-4 mr-2" />
                            Carta de Correção
                          </DropdownMenuItem>
                        )}
                        
                        {/* Ver Cartas de Correção */}
                        {nota.cartas_correcao?.length > 0 && (
                          <DropdownMenuItem onClick={() => carregarCartasCorrecao(nota)}>
                            <FileText className="h-4 w-4 mr-2" />
                            Ver CC-e ({nota.cartas_correcao.length})
                          </DropdownMenuItem>
                        )}
                        
                        {/* Cancelar - apenas para notas não canceladas */}
                        {nota.status !== 'cancelada' && (
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => { setNotaSelecionada(nota); setShowCancelar(true); }}
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Cancelar NF-e
                          </DropdownMenuItem>
                        )}
                        
                        {/* Excluir - notas NÃO autorizadas/canceladas (simulada, erro, rejeitada, pendente) — devolve o número à fila */}
                        {nota.status !== 'autorizada' && nota.status !== 'cancelada' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => { setNotaSelecionada(nota); setShowConfirmExcluir(true); }}
                              data-testid={`menu-excluir-${nota.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir (devolve o número)
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Erro de Reenvio com Detalhes */}
      <Dialog open={showErroReenvio} onOpenChange={setShowErroReenvio}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {erroReenvio?.tipo === 'validacao' ? (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  <span className="text-amber-700">{erroReenvio?.titulo || 'Configuração Incompleta'}</span>
                </>
              ) : erroReenvio?.tipo === 'simulacao' ? (
                <>
                  <FileWarning className="h-5 w-5 text-yellow-500" />
                  <span className="text-yellow-700">{erroReenvio?.titulo || 'Nota em Simulação'}</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700">{erroReenvio?.titulo || 'Erro'}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Mensagem Principal */}
            <Alert variant={erroReenvio?.tipo === 'validacao' ? 'default' : erroReenvio?.tipo === 'simulacao' ? 'warning' : 'destructive'} 
                   className={erroReenvio?.tipo === 'validacao' ? 'border-amber-300 bg-amber-50' : ''}>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>O que aconteceu?</AlertTitle>
              <AlertDescription className="mt-1">{erroReenvio?.mensagem}</AlertDescription>
            </Alert>
            
            {/* Próximo Passo (apenas para validação) */}
            {erroReenvio?.proximoPasso && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-sm text-blue-800 mb-1">💡 Próximo passo:</p>
                <p className="text-sm text-blue-700">{erroReenvio.proximoPasso}</p>
              </div>
            )}
            
            {/* Lista de Erros Detalhados */}
            {erroReenvio?.detalhes?.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-sm">Detalhes dos problemas:</p>
                <div className="p-3 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                  <ul className="list-none text-sm space-y-2">
                    {erroReenvio.detalhes.map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700">
                        <span className="flex-shrink-0">{d.startsWith('⚠️') || d.startsWith('❌') || d.startsWith('ℹ️') ? '' : '•'}</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {/* Lista de Erros Estruturados (validação) */}
            {erroReenvio?.erros?.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-sm flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Problemas a resolver ({erroReenvio.erros.length}):
                </p>
                <div className="space-y-2">
                  {erroReenvio.erros.slice(0, 5).map((erro, idx) => (
                    <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                      <p className="font-medium text-red-800">{erro.campo}</p>
                      <p className="text-red-700">{erro.mensagem}</p>
                      {erro.onde_corrigir && (
                        <p className="text-red-600 text-xs mt-1">📍 {erro.onde_corrigir}</p>
                      )}
                    </div>
                  ))}
                  {erroReenvio.erros.length > 5 && (
                    <p className="text-sm text-slate-500 italic">...e mais {erroReenvio.erros.length - 5} problema(s)</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Sugestões */}
            {erroReenvio?.sugestoes?.length > 0 && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="font-medium text-sm mb-2 flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  Como resolver:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 text-emerald-700">
                  {erroReenvio.sugestoes.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Dados Atuais da Empresa (para validação) */}
            {erroReenvio?.dadosAtuais && (
              <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg">
                <p className="font-medium text-sm mb-2 text-slate-700">📋 Dados Atuais da Empresa:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>CNPJ: <span className={erroReenvio.dadosAtuais.cnpj?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.cnpj}</span></div>
                  <div>UF: <span className={erroReenvio.dadosAtuais.uf?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.uf}</span></div>
                  <div>Logradouro: <span className={erroReenvio.dadosAtuais.logradouro?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.logradouro}</span></div>
                  <div>Nº: <span className={erroReenvio.dadosAtuais.numero?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.numero}</span></div>
                  <div>Cidade: <span className={erroReenvio.dadosAtuais.cidade?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.cidade}</span></div>
                  <div>Cód IBGE: <span className={erroReenvio.dadosAtuais.codIbge?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.codIbge}</span></div>
                  <div>CEP: <span className={erroReenvio.dadosAtuais.cep?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-600'}>{erroReenvio.dadosAtuais.cep}</span></div>
                </div>
              </div>
            )}
            
            {/* Produtos com Problema (para validação) */}
            {erroReenvio?.produtos?.length > 0 && (
              <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg">
                <p className="font-medium text-sm mb-2 text-slate-700">📦 Produtos da NF-e:</p>
                <div className="space-y-1 text-xs">
                  {erroReenvio.produtos.map((p, i) => (
                    <div key={i} className="flex justify-between items-center p-1 bg-white rounded">
                      <span className="truncate max-w-[150px]">{p.nome}</span>
                      <div className="flex gap-2">
                        <span className={p.ncm?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-500'}>NCM: {p.ncm}</span>
                        <span className={p.cfop?.includes('❌') ? 'text-red-600 font-medium' : 'text-slate-500'}>CFOP: {p.cfop}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mostrar pendências do diagnóstico (para modo simulação) */}
            {erroReenvio?.tipo === 'simulacao' && problemasDiagnostico.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-medium text-sm mb-2 flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="h-4 w-4" />
                  Pendências de configuração:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 text-yellow-700">
                  {problemasDiagnostico.slice(0, 5).map((p, i) => (
                    <li key={i}>{typeof p === 'string' ? p : `${p.campo}: ${p.mensagem}`}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Código SEFAZ (se houver) */}
            {erroReenvio?.codigoSefaz && (
              <div className="text-xs text-slate-500 p-2 bg-slate-100 rounded">
                Código SEFAZ: <code className="font-mono">{erroReenvio.codigoSefaz}</code>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowErroReenvio(false)}>
              Fechar
            </Button>
            <Button onClick={() => { setShowErroReenvio(false); window.location.href = '/configuracoes'; }}>
              Ir para Configurações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StatusIcon nota={notaSelecionada || {}} />
              Detalhes da NF-e #{notaSelecionada?.numero}
            </DialogTitle>
          </DialogHeader>
          {notaSelecionada && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Número/Série</Label>
                  <p className="font-medium">{notaSelecionada.numero} / {notaSelecionada.serie || 1}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p>{getStatusBadge(notaSelecionada)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Chave de Acesso</Label>
                  <p className="font-mono text-xs break-all">{notaSelecionada.chave || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Protocolo</Label>
                  <p className="font-mono">{notaSelecionada.protocolo || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Data Emissão</Label>
                  <p>{formatarData(notaSelecionada.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Valor Total</Label>
                  <p className="font-bold text-lg">{formatarValor(notaSelecionada.totais?.valor_total)}</p>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-muted-foreground">Destinatário</Label>
                <div className="p-3 bg-accent rounded-lg mt-1">
                  <p className="font-medium">{notaSelecionada.destinatario?.nome || 'Não informado'}</p>
                  <p className="text-sm text-muted-foreground">
                    {notaSelecionada.destinatario?.cpf_cnpj || 'CPF/CNPJ não informado'}
                  </p>
                  {notaSelecionada.destinatario?.endereco && (
                    <p className="text-sm text-muted-foreground">
                      {notaSelecionada.destinatario.endereco.logradouro}, {notaSelecionada.destinatario.endereco.numero} - 
                      {notaSelecionada.destinatario.endereco.bairro} - {notaSelecionada.destinatario.endereco.cidade}/{notaSelecionada.destinatario.endereco.uf}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Itens ({notaSelecionada.itens?.length || 0})</Label>
                <div className="space-y-2 mt-2">
                  {notaSelecionada.itens?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-accent rounded">
                      <div>
                        <p className="font-medium">{item.descricao}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantidade} x {formatarValor(item.valor_unitario)}
                        </p>
                      </div>
                      <p className="font-medium">{formatarValor(item.valor_total)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {notaSelecionada.nfe_erro && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Erro na Emissão</AlertTitle>
                  <AlertDescription>{notaSelecionada.nfe_erro}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetalhes(false)}>Fechar</Button>
            <Button variant="outline" onClick={() => imprimirDANFE(notaSelecionada)} className="gap-2">
              <Printer className="h-4 w-4" />
              Imprimir DANFE
            </Button>
            <Button onClick={() => downloadXML(notaSelecionada)} className="gap-2">
              <Download className="h-4 w-4" />
              Baixar XML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Exclusão */}
      <Dialog open={showConfirmExcluir} onOpenChange={setShowConfirmExcluir}>
        <DialogContent data-testid="dialog-confirm-excluir-nfe">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a NF-e #{notaSelecionada?.numero}?
              <br /><br />
              <strong className="text-green-700">💡 O número {notaSelecionada?.numero} será devolvido à fila</strong> e poderá ser reutilizado na próxima emissão — assim você não perde a numeração.
              <br /><br />
              <strong className="text-yellow-600">Atenção:</strong> Esta ação não pode ser desfeita.
              Apenas NF-e não autorizadas podem ser excluídas (simuladas, rejeitadas, com erro ou pendentes).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmExcluir(false)} disabled={processando} data-testid="btn-cancelar-excluir-nfe">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={excluirNota} disabled={processando} className="gap-2" data-testid="btn-confirm-excluir-nfe">
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Excluir e devolver número
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cancelar NF-e */}
      <Dialog open={showCancelar} onOpenChange={setShowCancelar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              Cancelar NF-e #{notaSelecionada?.numero}
            </DialogTitle>
            <DialogDescription>
              O cancelamento da NF-e é irreversível. Informe a justificativa para o cancelamento.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Atenção</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                  <li>O prazo máximo para cancelamento é de 24 horas após a autorização</li>
                  <li>A justificativa deve ter no mínimo 15 caracteres</li>
                  <li>O cancelamento será registrado na SEFAZ</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label htmlFor="justificativa">Justificativa do Cancelamento *</Label>
              <Textarea
                id="justificativa"
                placeholder="Informe o motivo do cancelamento (mínimo 15 caracteres)"
                value={justificativaCancelamento}
                onChange={(e) => setJustificativaCancelamento(e.target.value)}
                className="mt-2"
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {justificativaCancelamento.length}/15 caracteres mínimos
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelar(false); setJustificativaCancelamento(''); }} disabled={processando}>
              Voltar
            </Button>
            <Button 
              variant="destructive" 
              onClick={cancelarNota} 
              disabled={processando || justificativaCancelamento.length < 15}
              className="gap-2"
            >
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Inutilizar Numeração */}
      <Dialog open={showInutilizar} onOpenChange={setShowInutilizar}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileX className="h-5 w-5" />
              Inutilizar Numeração de NF-e
            </DialogTitle>
            <DialogDescription>
              Use esta função quando houver quebra de sequência na numeração das NF-e.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Quando usar?</AlertTitle>
              <AlertDescription>
                A inutilização é necessária quando números de NF-e foram pulados por erro de sistema, 
                falha de energia, ou outros motivos que causaram quebra na sequência.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="serie">Série</Label>
                <Input
                  id="serie"
                  type="number"
                  min="1"
                  value={inutilizarForm.serie}
                  onChange={(e) => setInutilizarForm({...inutilizarForm, serie: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="numero_inicial">Número Inicial</Label>
                <Input
                  id="numero_inicial"
                  type="number"
                  min="1"
                  placeholder="Ex: 100"
                  value={inutilizarForm.numero_inicial}
                  onChange={(e) => setInutilizarForm({...inutilizarForm, numero_inicial: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="numero_final">Número Final</Label>
                <Input
                  id="numero_final"
                  type="number"
                  min="1"
                  placeholder="Ex: 105"
                  value={inutilizarForm.numero_final}
                  onChange={(e) => setInutilizarForm({...inutilizarForm, numero_final: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="justificativa_inut">Justificativa *</Label>
              <Textarea
                id="justificativa_inut"
                placeholder="Informe o motivo da inutilização (mínimo 15 caracteres)"
                value={inutilizarForm.justificativa}
                onChange={(e) => setInutilizarForm({...inutilizarForm, justificativa: e.target.value})}
                className="mt-2"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {inutilizarForm.justificativa.length}/15 caracteres mínimos
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { 
                setShowInutilizar(false); 
                setInutilizarForm({ serie: 1, numero_inicial: '', numero_final: '', justificativa: '' }); 
              }} 
              disabled={processando}
            >
              Cancelar
            </Button>
            <Button 
              onClick={inutilizarNumeracao} 
              disabled={processando || inutilizarForm.justificativa.length < 15 || !inutilizarForm.numero_inicial || !inutilizarForm.numero_final}
              className="gap-2"
            >
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileX className="h-4 w-4" />}
              Inutilizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Carta de Correção */}
      <Dialog open={showCartaCorrecao} onOpenChange={setShowCartaCorrecao}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Carta de Correção - NF-e #{notaSelecionada?.numero}
            </DialogTitle>
            <DialogDescription>
              A Carta de Correção permite corrigir erros em campos específicos da NF-e.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>O que NÃO pode ser corrigido:</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                  <li>Valores (base de cálculo, alíquotas, valor da operação)</li>
                  <li>Dados que alterem remetente ou destinatário</li>
                  <li>Data de emissão ou saída</li>
                  <li>Quantidade de itens</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            {notaSelecionada?.cartas_correcao?.length > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <p className="text-sm">
                  <strong>Cartas existentes:</strong> {notaSelecionada.cartas_correcao.length} de 20
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="correcao">Texto da Correção *</Label>
              <Textarea
                id="correcao"
                placeholder="Descreva a correção a ser realizada (mínimo 15 caracteres, máximo 1000)"
                value={textoCorrecao}
                onChange={(e) => setTextoCorrecao(e.target.value)}
                className="mt-2"
                rows={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {textoCorrecao.length}/1000 caracteres ({textoCorrecao.length < 15 ? `faltam ${15 - textoCorrecao.length}` : 'OK'})
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowCartaCorrecao(false); setTextoCorrecao(''); }} 
              disabled={processando}
            >
              Cancelar
            </Button>
            <Button 
              onClick={emitirCartaCorrecao} 
              disabled={processando || textoCorrecao.length < 15 || textoCorrecao.length > 1000}
              className="gap-2"
            >
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit3 className="h-4 w-4" />}
              Emitir CC-e
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Lista de Cartas de Correção */}
      <Dialog open={showListaCartas} onOpenChange={setShowListaCartas}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Cartas de Correção - NF-e #{notaSelecionada?.numero}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {cartasCorrecaoLista.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Nenhuma Carta de Correção registrada
              </p>
            ) : (
              cartasCorrecaoLista.map((cce, idx) => (
                <div key={idx} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline">CC-e #{cce.seq_evento}</Badge>
                    {cce.simulado && (
                      <Badge variant="secondary" className="text-xs">Simulado</Badge>
                    )}
                  </div>
                  <p className="text-sm mb-2">{cce.correcao}</p>
                  <div className="text-xs text-muted-foreground">
                    <span>Protocolo: {cce.protocolo || 'N/A'}</span>
                    <span className="mx-2">•</span>
                    <span>{cce.data_evento ? new Date(cce.data_evento).toLocaleString('pt-BR') : '-'}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowListaCartas(false)}>
              Fechar
            </Button>
            {cartasCorrecaoLista.length < 20 && (
              <Button onClick={() => { setShowListaCartas(false); setShowCartaCorrecao(true); }} className="gap-2">
                <Edit3 className="h-4 w-4" />
                Nova CC-e
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Itens (corrigir descrição, NCM, CFOP antes de enviar SEFAZ) */}
      <Dialog open={showEditarItens} onOpenChange={setShowEditarItens}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✏️ Editar Itens da NF-e</DialogTitle>
            <DialogDescription>
              Corrija descrição, NCM (8 dígitos), CFOP (4 dígitos), CST/CSOSN, quantidade e valor antes de transmitir à SEFAZ.
              Os totais serão recalculados automaticamente ao salvar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {itensEditados.map((item, idx) => (
              <div
                key={idx}
                className="border rounded-lg p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start bg-slate-50"
                data-testid={`editar-item-row-${idx}`}
              >
                <div className="md:col-span-12 flex justify-between items-center">
                  <span className="font-semibold text-sm text-slate-700">
                    Item {idx + 1}
                  </span>
                  <span className="text-xs text-slate-500">
                    Total: R$ {(Number(item.quantidade || 0) * Number(item.valor_unitario || 0)).toFixed(2)}
                  </span>
                </div>

                <div className="md:col-span-6">
                  <label className="text-xs font-medium text-slate-600">Descrição *</label>
                  <input
                    type="text"
                    value={item.descricao || ''}
                    onChange={(e) => atualizarItemEditado(idx, 'descricao', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                    data-testid={`edit-item-descricao-${idx}`}
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600">NCM * (8 dígitos)</label>
                  <input
                    type="text"
                    maxLength={8}
                    value={item.ncm || ''}
                    onChange={(e) =>
                      atualizarItemEditado(idx, 'ncm', e.target.value.replace(/[^0-9]/g, ''))
                    }
                    className="w-full px-2 py-1 border rounded text-sm font-mono"
                    placeholder="00000000"
                    data-testid={`edit-item-ncm-${idx}`}
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600">CFOP * (4 dígitos)</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={item.cfop || ''}
                    onChange={(e) =>
                      atualizarItemEditado(idx, 'cfop', e.target.value.replace(/[^0-9]/g, ''))
                    }
                    className="w-full px-2 py-1 border rounded text-sm font-mono"
                    placeholder="5102"
                    data-testid={`edit-item-cfop-${idx}`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-slate-600">CST</label>
                  <input
                    type="text"
                    value={item.cst || ''}
                    onChange={(e) => atualizarItemEditado(idx, 'cst', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm font-mono"
                    placeholder="00"
                    data-testid={`edit-item-cst-${idx}`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-slate-600">CSOSN</label>
                  <input
                    type="text"
                    value={item.csosn || ''}
                    onChange={(e) => atualizarItemEditado(idx, 'csosn', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm font-mono"
                    placeholder="102"
                    data-testid={`edit-item-csosn-${idx}`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-slate-600">Unidade</label>
                  <input
                    type="text"
                    value={item.unidade || ''}
                    onChange={(e) => atualizarItemEditado(idx, 'unidade', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="UN"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600">Quantidade *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={item.quantidade || 0}
                    onChange={(e) =>
                      atualizarItemEditado(idx, 'quantidade', Number(e.target.value))
                    }
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600">Valor Unitário *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.valor_unitario || 0}
                    onChange={(e) =>
                      atualizarItemEditado(idx, 'valor_unitario', Number(e.target.value))
                    }
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditarItens(false)}
              disabled={salvandoEdicao}
              data-testid="editar-itens-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={salvarEdicaoItens}
              disabled={salvandoEdicao}
              data-testid="editar-itens-salvar"
            >
              {salvandoEdicao ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</>
              ) : (
                <>💾 Salvar Itens</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Debug XML (mostra o XML completo gerado, sem transmitir) */}
      <Dialog open={showDebugXml} onOpenChange={setShowDebugXml}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="dialog-debug-xml">
          <DialogHeader>
            <DialogTitle>🔍 Debug XML — NF-e #{notaSelecionada?.numero}</DialogTitle>
            <DialogDescription>
              XML completo que seria enviado à SEFAZ. Use para diagnosticar erros de schema (225).
              Copie e envie para o suporte se precisar de ajuda.
            </DialogDescription>
          </DialogHeader>
          {debugXmlLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-600">Gerando XML...</span>
            </div>
          ) : (
            <textarea
              readOnly
              value={debugXmlContent}
              className="flex-1 font-mono text-xs p-3 border rounded bg-slate-50 min-h-[60vh] w-full resize-none"
              data-testid="debug-xml-textarea"
              onClick={(e) => e.target.select()}
            />
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Migrar CRT de todas as NF-e não autorizadas para o regime atual da empresa? Útil para corrigir notas antigas com CRT errado (Rejeição 225).')) return;
                try {
                  const r = await api.post('/nfe/admin/migrar-crt');
                  toast({ title: '✅ Migração concluída', description: r?.data?.message || 'Pronto' });
                  carregarNotas();
                  setShowDebugXml(false);
                } catch (e) {
                  toast({ title: 'Erro', description: e?.response?.data?.detail || 'Falha na migração', variant: 'destructive' });
                }
              }}
              data-testid="debug-xml-migrar-crt"
            >
              🔧 Corrigir CRT antigo
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(debugXmlContent);
                toast({ title: '📋 XML copiado!', description: 'Já está na área de transferência.' });
              }}
              data-testid="debug-xml-copy"
            >
              📋 Copiar tudo
            </Button>
            <Button variant="outline" onClick={() => setShowDebugXml(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Cabeçalho da NF-e */}
      <Dialog open={showEditarCabecalho} onOpenChange={setShowEditarCabecalho}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-editar-cabecalho">
          <DialogHeader>
            <DialogTitle>📝 Editar Cabeçalho — NF-e #{notaSelecionada?.numero}</DialogTitle>
            <DialogDescription>
              Corrija a natureza da operação, destinatário, frete e informações adicionais antes de transmitir à SEFAZ.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Natureza da Operação</Label>
              <Input
                value={cabecalhoEditado.natureza_operacao}
                onChange={(e) => setCabecalhoEditado({ ...cabecalhoEditado, natureza_operacao: e.target.value })}
                placeholder="Ex: VENDA, REMESSA, DEVOLUCAO"
                maxLength={60}
                data-testid="input-natureza-operacao"
              />
            </div>

            <Separator />

            <div>
              <Label className="text-base font-semibold">Destinatário</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">CPF / CNPJ</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.cpf_cnpj}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: { ...cabecalhoEditado.destinatario, cpf_cnpj: e.target.value }
                    })}
                    placeholder="11 ou 14 dígitos"
                    data-testid="input-dest-cpf-cnpj"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nome / Razão Social</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.nome}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: { ...cabecalhoEditado.destinatario, nome: e.target.value }
                    })}
                    maxLength={60}
                    data-testid="input-dest-nome"
                  />
                </div>
                <div>
                  <Label className="text-xs">Inscrição Estadual</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.ie}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: { ...cabecalhoEditado.destinatario, ie: e.target.value }
                    })}
                    placeholder="Vazio para ISENTO"
                    data-testid="input-dest-ie"
                  />
                </div>
                <div>
                  <Label className="text-xs">Indicador IE Dest</Label>
                  <Select
                    value={cabecalhoEditado.destinatario.indIEDest}
                    onValueChange={(v) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: { ...cabecalhoEditado.destinatario, indIEDest: v }
                    })}
                  >
                    <SelectTrigger data-testid="select-dest-indIEDest"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Contribuinte ICMS</SelectItem>
                      <SelectItem value="2">2 - Contribuinte Isento de IE</SelectItem>
                      <SelectItem value="9">9 - Não Contribuinte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Label className="text-sm font-semibold mt-3 block">Endereço</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
                <div className="md:col-span-2">
                  <Label className="text-xs">Logradouro</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.logradouro}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, logradouro: e.target.value }
                      }
                    })}
                    data-testid="input-dest-logradouro"
                  />
                </div>
                <div>
                  <Label className="text-xs">Número</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.numero}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, numero: e.target.value }
                      }
                    })}
                    data-testid="input-dest-numero"
                  />
                </div>
                <div>
                  <Label className="text-xs">Complemento</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.complemento}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, complemento: e.target.value }
                      }
                    })}
                    data-testid="input-dest-complemento"
                  />
                </div>
                <div>
                  <Label className="text-xs">Bairro</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.bairro}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, bairro: e.target.value }
                      }
                    })}
                    data-testid="input-dest-bairro"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cidade</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.cidade}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, cidade: e.target.value }
                      }
                    })}
                    data-testid="input-dest-cidade"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cód. Município (IBGE)</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.cod_municipio}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, cod_municipio: e.target.value }
                      }
                    })}
                    placeholder="7 dígitos"
                    data-testid="input-dest-cod-municipio"
                  />
                </div>
                <div>
                  <Label className="text-xs">UF</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.uf}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, uf: (e.target.value || '').toUpperCase().slice(0, 2) }
                      }
                    })}
                    maxLength={2}
                    data-testid="input-dest-uf"
                  />
                </div>
                <div>
                  <Label className="text-xs">CEP</Label>
                  <Input
                    value={cabecalhoEditado.destinatario.endereco.cep}
                    onChange={(e) => setCabecalhoEditado({
                      ...cabecalhoEditado,
                      destinatario: {
                        ...cabecalhoEditado.destinatario,
                        endereco: { ...cabecalhoEditado.destinatario.endereco, cep: e.target.value }
                      }
                    })}
                    placeholder="8 dígitos"
                    data-testid="input-dest-cep"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <Label>Modalidade do Frete</Label>
              <Select
                value={cabecalhoEditado.frete.modalidade}
                onValueChange={(v) => setCabecalhoEditado({ ...cabecalhoEditado, frete: { modalidade: v } })}
              >
                <SelectTrigger data-testid="select-frete-modalidade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - Por conta do Emitente</SelectItem>
                  <SelectItem value="1">1 - Por conta do Destinatário</SelectItem>
                  <SelectItem value="2">2 - Por conta de Terceiros</SelectItem>
                  <SelectItem value="3">3 - Transporte Próprio (Remetente)</SelectItem>
                  <SelectItem value="4">4 - Transporte Próprio (Destinatário)</SelectItem>
                  <SelectItem value="9">9 - Sem Frete</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Informações Adicionais (opcional, máx. 5000 caracteres)</Label>
              <Textarea
                rows={3}
                value={cabecalhoEditado.info_adicionais}
                onChange={(e) => setCabecalhoEditado({ ...cabecalhoEditado, info_adicionais: e.target.value })}
                maxLength={5000}
                data-testid="textarea-info-adicionais"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarCabecalho(false)} disabled={salvandoCabecalho} data-testid="btn-cancelar-editar-cabecalho">
              Cancelar
            </Button>
            <Button onClick={salvarCabecalho} disabled={salvandoCabecalho} className="gap-2" data-testid="btn-salvar-cabecalho">
              {salvandoCabecalho ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Salvando...</>
              ) : (
                <>💾 Salvar Cabeçalho</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Resultado Consulta SEFAZ */}
      <Dialog open={showConsultaSefaz} onOpenChange={setShowConsultaSefaz}>
        <DialogContent className="max-w-lg" data-testid="consulta-sefaz-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" /> Situação na SEFAZ — NF-e #{consultaSefazResult?.numero}
            </DialogTitle>
            <DialogDescription>
              Consulta oficial pela chave de acesso (WebService NFeConsultaProtocolo4).
            </DialogDescription>
          </DialogHeader>
          {consultaSefazResult && (
            <div className="space-y-3">
              {consultaSefazResult.existe_na_sefaz ? (
                <Alert className="border-emerald-200 bg-emerald-50">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertTitle className="text-emerald-800">Nota encontrada na SEFAZ</AlertTitle>
                  <AlertDescription className="text-emerald-700">
                    {consultaSefazResult.autorizada ? 'Autorizada' : 'Existe'} na base da SEFAZ.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Nota NÃO encontrada na SEFAZ</AlertTitle>
                  <AlertDescription>
                    A SEFAZ não encontrou esta nota neste ambiente. Verifique se o ambiente de emissão
                    (Homologação x Produção) está correto — notas de homologação não aparecem no portal de produção.
                  </AlertDescription>
                </Alert>
              )}
              <div className="rounded-lg border p-3 text-sm space-y-1 bg-slate-50">
                <div className="flex justify-between"><span className="text-slate-500">Ambiente</span><span className="font-medium">{consultaSefazResult.tp_amb === '1' ? 'Produção' : 'Homologação'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Código SEFAZ (cStat)</span><span className="font-medium" data-testid="consulta-sefaz-cstat">{consultaSefazResult.codigo}</span></div>
                <div className="flex justify-between gap-4"><span className="text-slate-500">Mensagem</span><span className="font-medium text-right">{consultaSefazResult.mensagem}</span></div>
                {consultaSefazResult.protocolo && (
                  <div className="flex justify-between"><span className="text-slate-500">Protocolo</span><span className="font-medium">{consultaSefazResult.protocolo}</span></div>
                )}
                {consultaSefazResult.http_status && (
                  <div className="flex justify-between"><span className="text-slate-500">HTTP SEFAZ</span><span className="font-medium">{consultaSefazResult.http_status}</span></div>
                )}
                <div className="flex justify-between gap-4"><span className="text-slate-500">Chave</span><span className="font-mono text-xs break-all text-right">{consultaSefazResult.chave}</span></div>
              </div>
              {consultaSefazResult.xml_resposta && (
                <details className="rounded-lg border p-2 text-xs bg-slate-900 text-slate-100" data-testid="consulta-sefaz-raw">
                  <summary className="cursor-pointer text-slate-300 select-none">Ver resposta técnica da SEFAZ</summary>
                  <pre className="mt-2 whitespace-pre-wrap break-all max-h-64 overflow-auto">{consultaSefazResult.xml_resposta}</pre>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsultaSefaz(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GerenciarNFePage;
